import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import { HedgeFormModel } from '@/features/hedge/types'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { Position } from '@/types/generated/common/trading'
import { Exchange, Instrument, InstrumentKind } from '@/types/generated/common/types'

// Helpers for the hedge instrument override: deriving the underlying of a symbol, the
// default perpetual the backend will pick, and whether a given scope even permits an
// explicit instrument (it only does for a single-underlying scope).

// The underlying asset of a symbol = the leading token before the first '-' or '_'. Pure
// string-parse fallback only — some venues' own canonical base currency contains an
// underscore (e.g. HYPE_USDC), which this naive split always strips, so real code should
// prefer resolvePositionUnderlying (metadata-first) and fall back to this only when no
// Instrument metadata is available for the symbol at all.
// e.g. BTC-PERPETUAL -> BTC, BTC_USDC-PERPETUAL -> BTC, ETH-USDC -> ETH.
export function underlyingOf(symbol: string): string {
  return (symbol.split(/[-_]/)[0] ?? '').toUpperCase()
}

// A hedge instrument may only be a spot or perpetual variant — never an option or a
// dated future. Mirrors the backend's accepted set.
export function isHedgeableInstrument(i: Instrument): boolean {
  if (isPerpInstrument(i)) return true
  return i.kind === InstrumentKind.INSTRUMENT_KIND_SPOT
}

// Every underlying the current exchange offers a hedgeable (spot or perp) instrument for —
// unlike distinctPositionUnderlyings, not limited to underlyings with an open position. Lets
// the Target picker offer an asset "in advance", before any exposure exists yet.
export function distinctHedgeableUnderlyings(instruments: Instrument[]): string[] {
  const set = new Set<string>()
  for (const i of instruments) {
    if (!i.isActive || !isHedgeableInstrument(i)) continue
    const base = (i.baseCurrency || underlyingOf(i.symbol)).toUpperCase()
    if (base) set.add(base)
  }
  return Array.from(set).sort()
}

// Distinct underlyings represented by a set of position symbols. String-parse only — for
// real positions, prefer distinctPositionUnderlyings (metadata-first via each position's own
// exchange instrument map). Kept for callers with no Position/map context available.
export function distinctUnderlyings(symbols: string[]): string[] {
  return Array.from(new Set(symbols.map(underlyingOf).filter(Boolean)))
}

// A position's true underlying: Instrument.baseCurrency (via that position's own exchange
// instrument map) first, falling back to underlyingOf's string-parse only when no metadata
// is found — same "metadata first, parse fallback" pattern as resolveBaseCurrency itself
// (features/order/order-form/lib/instrument-resolution.ts), which this wraps.
export function resolvePositionUnderlying(
  p: Position,
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): string {
  const map = multiExchangeInstrumentMap.get(p.exchange) ?? instrumentMap
  return resolveBaseCurrency(map, p.symbol)
}

// Metadata-based sibling of distinctUnderlyings — takes real positions instead of bare
// symbols so it can resolve baseCurrency per-exchange via resolvePositionUnderlying.
export function distinctPositionUnderlyings(
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): string[] {
  return Array.from(
    new Set(
      positions
        .map((p) => resolvePositionUnderlying(p, instrumentMap, multiExchangeInstrumentMap))
        .filter(Boolean)
    )
  )
}

// Best-effort preview of what the backend will derive for an underlying: its perpetual.
// Prefers the canonical `${UNDERLYING}-PERPETUAL`, else the shortest matching perp.
export function resolveDefaultHedgeInstrument(
  instruments: Instrument[],
  underlying: string
): Instrument | undefined {
  const u = underlying.toUpperCase()
  if (!u) return undefined
  const perps = instruments.filter(
    (i) =>
      i.isActive &&
      isPerpInstrument(i) &&
      (i.baseCurrency || underlyingOf(i.symbol)).toUpperCase() === u
  )
  if (perps.length === 0) return undefined
  const canonical = perps.find((i) => i.symbol.toUpperCase() === `${u}-PERPETUAL`)
  if (canonical) return canonical
  return [...perps].sort(
    (a, b) => a.symbol.length - b.symbol.length || a.symbol.localeCompare(b.symbol)
  )[0]
}

export interface HedgeInstrumentScope {
  // The single underlying an explicit instrument must match, or null when the scope spans
  // multiple underlyings (whole portfolio / mixed group). In that case an explicit
  // instrument is invalid and the backend expands the hedge per underlying.
  underlying: string | null
  // Whether the scope permits an explicit instrument override at all.
  allowsExplicit: boolean
}

// scopeMode 'underlying' just echoes the user-picked asset — no position/map lookup needed.
function resolveExplicitUnderlyingScope(form: HedgeFormModel): HedgeInstrumentScope {
  const u = form.underlyingAsset.trim().toUpperCase()
  return { underlying: u || null, allowsExplicit: u.length > 0 }
}

// scopeMode 'positionGroup': derive the group's distinct underlying(s) from its actual
// matched positions (not the raw stored symbols) via metadata-first resolution, so a stale
// symbol left in the group can't conjure a phantom asset with no real position behind it.
//
// Matches form.positionSymbols against positions by symbol alone — if the same symbol is
// open on two different accounts/exchanges, they'd be conflated here (positionSymbolOptions,
// the picker UI, doesn't disambiguate by venue either). Pre-existing limitation of the
// position-group model, not something this function introduces or resolves.
function resolvePositionGroupScope(
  form: HedgeFormModel,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeInstrumentScope {
  const matched = positions.filter((p) => form.positionSymbols.includes(p.symbol))
  const us = distinctPositionUnderlyings(matched, instrumentMap, multiExchangeInstrumentMap)
  if (us.length === 1) return { underlying: us[0], allowsExplicit: true }
  return { underlying: null, allowsExplicit: false }
}

export function hedgeInstrumentScope(
  form: HedgeFormModel,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeInstrumentScope {
  if (form.scopeMode === 'underlying') return resolveExplicitUnderlyingScope(form)
  if (form.scopeMode === 'positionGroup') {
    return resolvePositionGroupScope(form, positions, instrumentMap, multiExchangeInstrumentMap)
  }
  // Whole portfolio: backend expands per open underlying; an explicit instrument is invalid.
  return { underlying: null, allowsExplicit: false }
}
